const upper = value => String(value ?? '').trim().toUpperCase();
const compact = value => upper(value).replace(/[\s-]+/g, '');

function articleVariants(value) {
  const raw = upper(value).replace(/^EXP[\s-]*/, '');
  const variants = new Set([raw]);
  const parts = raw.split('-');
  if (parts.length === 4 && /^\d{2,3}$/.test(parts[3])) {
    const normalized = [...parts.slice(0, 3), parts[3].slice(0, 2)].join('-');
    variants.add(normalized);
    if (/^1\d{4}-/.test(normalized)) variants.add(normalized.slice(1));
  }
  return [...variants];
}

export function codeOnlyMatches(rawValue, productCode) {
  return articleVariants(rawValue).some(value => compact(value) === compact(productCode));
}

export function fullBarcodeMatches(rawValue, item) {
  const raw = upper(rawValue).replace(/^EXP[\s-]*/, '');
  if (!raw || !item?.product_code || !item?.color || !item?.size) return false;
  if (compact(raw) === compact(`${item.product_code}${item.color}${item.size}`)) return true;
  const parts = raw.split('-');
  return parts.length >= 6 && codeOnlyMatches(parts.slice(0, 4).join('-'), item.product_code)
    && compact(parts[4]) === compact(item.color)
    && compact(parts.slice(5).join('')) === compact(item.size);
}

// A code-only scan needs a deliberate colour and size choice. A complete scan
// resolves its plan line directly; a wrong suffix must never fall back to code-only.
export function resolvePlanBarcode(rawValue, items) {
  const raw = upper(rawValue).replace(/^EXP[\s-]*/, '');
  if (!raw) return { kind: 'invalid', candidates: [] };
  const exact = items.filter(item => fullBarcodeMatches(raw, item));
  if (exact.length === 1) return { kind: 'exact', item: exact[0], candidates: exact };
  if (exact.length > 1) return { kind: 'ambiguous', candidates: exact };
  const candidates = items.filter(item => codeOnlyMatches(raw, item.product_code));
  return candidates.length ? { kind: 'select', candidates } : { kind: 'invalid', candidates: [] };
}

// Manual warehouse transfers use the same POS article / colour / size rules,
// but resolve against the selected warehouse's actual positive stock rows.
// A partial or wrong suffix must not quietly turn into a code-only scan.
export function resolveWarehouseStockBarcode(rawValue, stockRows) {
  const raw = upper(rawValue).replace(/^EXP[\s-]*/, '');
  if (!raw) return { kind: 'invalid', candidates: [] };
  const rows = (stockRows || []).filter(row => Number(row.qty) > 0).map(row => ({
    ...row,
    product_code: row.product_code ?? row.productCode,
  }));
  const exact = rows.filter(row => fullBarcodeMatches(raw, row));
  if (exact.length === 1) return { kind: 'exact', item: exact[0], candidates: exact };
  if (exact.length > 1) return { kind: 'ambiguous', candidates: exact };
  const candidates = rows.filter(row => codeOnlyMatches(raw, row.product_code));
  return candidates.length ? { kind: 'select', candidates } : { kind: 'invalid', candidates: [] };
}
